import type { DocIndexEntry } from "@/lib/docLinks";
import { getDocById, resolvePhrase } from "@/lib/docLinks";

const SKIP_TAGS = new Set(["A", "CODE", "PRE", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6"]);

// Broadened per spec: 2-4 uppercase letters, hyphen, digits.
const RAW_ID_RE = /\b([A-Z]{2,4}-\d{2,5})\b/g;
// "the X doc" / "the X Y guides" — 1 to 3 lowercase word tokens between "the" and doc(s)/guide(s).
const PHRASE_RE = /\bthe\s+((?:[a-z][a-z0-9-]*(?:\s+|-)){0,2}[a-z][a-z0-9-]*)\s+(?:docs?|guides?)\b/gi;
// "sample formats" fixed phrase → /resources (member-facing sample formats surface).
const SAMPLE_FORMATS_RE = /\bsample\s+formats\b/gi;

function collectTextNodes(root: HTMLElement): Text[] {
  const out: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      let cur: HTMLElement | null = p;
      while (cur && cur !== root) {
        if (SKIP_TAGS.has(cur.tagName)) return NodeFilter.FILTER_REJECT;
        if (cur.classList && cur.classList.contains("doc-link")) return NodeFilter.FILTER_REJECT;
        cur = cur.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  } as any);
  let n: Node | null = walker.nextNode();
  while (n) {
    out.push(n as Text);
    n = walker.nextNode();
  }
  return out;
}

function makeAnchor(path: string, text: string, docId: string, title: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = "doc-link";
  a.href = path;
  a.textContent = text;
  a.setAttribute("data-doc-id", docId);
  // Display only: members never see the internal reference code.
  a.title = title;
  return a;
}


/**
 * Replace occurrences in a text node using a regex + a replacer that returns
 * either { anchor, matchLen } or null (leave literal). Returns whether any
 * replacement happened.
 */
function replaceInTextNode(
  tn: Text,
  re: RegExp,
  build: (match: RegExpExecArray) => HTMLAnchorElement | null,
): void {
  const parent = tn.parentNode;
  if (!parent) return;
  const original = tn.nodeValue || "";
  re.lastIndex = 0;
  let m: RegExpExecArray | null;
  const frags: Array<Node> = [];
  let lastIdx = 0;
  let hit = false;
  while ((m = re.exec(original)) !== null) {
    const a = build(m);
    if (!a) continue;
    hit = true;
    if (m.index > lastIdx) frags.push(document.createTextNode(original.slice(lastIdx, m.index)));
    frags.push(a);
    lastIdx = m.index + m[0].length;
  }
  if (!hit) return;
  if (lastIdx < original.length) frags.push(document.createTextNode(original.slice(lastIdx)));
  for (const f of frags) parent.insertBefore(f, tn);
  parent.removeChild(tn);
}

/** Step A: semantic phrases first (longer, greedier); Step B: raw IDs. */
export function applyDocLinks(
  root: HTMLElement,
  index: DocIndexEntry[],
  selfDocId?: string,
) {
  if (!root) return;

  // Semantic phrases
  for (const tn of collectTextNodes(root)) {
    replaceInTextNode(tn, new RegExp(PHRASE_RE.source, "gi"), (m) => {
      const inner = m[1];
      const entry = resolvePhrase(inner, index, selfDocId);
      if (!entry) return null;
      return makeAnchor(entry.path, m[0], entry.doc_id, entry.title);
    });
  }

  // Fixed phrase: "sample formats" → /resources
  for (const tn of collectTextNodes(root)) {
    replaceInTextNode(tn, new RegExp(SAMPLE_FORMATS_RE.source, "gi"), (m) => {
      const a = document.createElement("a");
      a.className = "doc-link";
      a.href = "/resources";
      a.textContent = m[0];
      a.title = "Sample formats";
      return a;
    });
  }

  // Raw IDs (recollect: DOM changed).
  for (const tn of collectTextNodes(root)) {
    replaceInTextNode(tn, new RegExp(RAW_ID_RE.source, "g"), (m) => {
      const id = m[1];
      if (selfDocId && id === selfDocId) return null;
      const entry = getDocById(id);
      if (!entry) return null;
      return makeAnchor(entry.path, entry.short_title, entry.doc_id, entry.title);
    });
  }
}
